import { useState, useEffect } from "react";
import {
  Box,
  Tabs,
  Tab,
  Chip,
  TextField,
  MenuItem,
  Button,
} from "@mui/material";
import {
  Visibility,
  Add,
  Search as SearchIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useRefresh } from "../../contexts/RefreshContext";
import theme from "../../theme/theme";
import SupportTicketService from "../../api/SupportTicketService";
import TableComponent from "../../components/Table";
import CreateTicketDialog from "../../components/support/CreateTicketDialog";

const STATUS_CONFIG = {
  open: { bg: "#EBF5FF", color: "#1976D2", label: "Open" },
  in_progress: { bg: "#FFF8E1", color: "#F57F17", label: "In Progress" },
  resolved: { bg: "#E8F5E9", color: "#2E7D32", label: "Resolved" },
  closed: { bg: "#F5F5F5", color: "#757575", label: "Closed" },
};

const PRIORITY_CONFIG = {
  low: { bg: "#E8F5E9", color: "#2E7D32" },
  medium: { bg: "#FFF8E1", color: "#F57F17" },
  high: { bg: "#FFF3E0", color: "#E65100" },
  urgent: { bg: "#FFEBEE", color: "#C62828" },
};

const initialFilters = {
  search: "",
  category: "",
  priority: "",
  from_date: "",
  to_date: "",
};

const TicketsManagementPage = () => {
  const navigate = useNavigate();
  const { refreshKey } = useRefresh();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [links, setLinks] = useState([]);
  const [meta, setMeta] = useState({ from: 0, to: 0, total: 0 });
  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState(initialFilters);
  const [counts, setCounts] = useState({
    all: 0,
    open: 0,
    in_progress: 0,
    resolved: 0,
    closed: 0,
  });
  const [exporting, setExporting] = useState(false);

  // Create dialog
  const [openCreate, setOpenCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createErrors, setCreateErrors] = useState({});

  const statusForTab = ["", "open", "in_progress", "resolved", "closed"];

  const loadCounts = async () => {
    try {
      const params = { ...filters };
      const res = await SupportTicketService.fetchTicketCounts(params);
      setCounts(res.data?.data || res.data || {});
    } catch {
      // silent
    }
  };

  const loadTickets = async () => {
    setLoading(true);
    try {
      const params = { ...filters, page: currentPage, per_page: perPage };
      const status = statusForTab[tabValue];
      if (status) params.status = status;

      const res = await SupportTicketService.fetchTickets(params);
      const payload = res.data || res;
      setTickets(payload?.data || []);
      setLinks(payload?.links || []);
      setMeta({ from: payload?.from || 0, to: payload?.to || 0, total: payload?.total || 0 });
    } catch (err) {
      toast.error(err.message || "Failed to fetch tickets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, tabValue, perPage]);

  useEffect(() => {
    loadTickets();
    loadCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, tabValue, refreshKey, currentPage, perPage]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setFilters({ ...filters, search: searchInput });
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setFilters({ ...filters, search: "" });
  };

  const handleClearFilters = () => {
    setSearchInput("");
    setFilters(initialFilters);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = { ...filters };
      const status = statusForTab[tabValue];
      if (status) params.status = status;

      const response = await SupportTicketService.exportTickets(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "support_tickets.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to export tickets");
    } finally {
      setExporting(false);
    }
  };

  const handleCreate = async (data) => {
    setCreating(true);
    setCreateErrors({});
    try {
      await SupportTicketService.createTicket(data);
      toast.success("Ticket created successfully");
      setOpenCreate(false);
      loadTickets();
      loadCounts();
    } catch (err) {
      if (err?.response?.data?.errors) {
        setCreateErrors(err.response.data.errors);
      } else {
        toast.error(err.message || "Failed to create ticket");
      }
    } finally {
      setCreating(false);
    }
  };

  const hasActiveFilters =
    filters.search ||
    filters.category ||
    filters.priority ||
    filters.from_date ||
    filters.to_date;

  const columns = [
    { field: "ticket_number", headerName: "Ticket #" },
    {
      field: "customer.name",
      headerName: "Customer",
      render: (r) => r.customer?.name || "-",
    },
    {
      field: "customer.phone",
      headerName: "Contact No",
      render: (r) => r.customer?.phone || "-",
    },
    { field: "subject", headerName: "Subject" },
    {
      field: "category",
      headerName: "Category",
      render: (r) => (
        <span style={{ textTransform: "capitalize" }}>{r.category}</span>
      ),
    },
    {
      field: "priority",
      headerName: "Priority",
      render: (r) => {
        const cfg = PRIORITY_CONFIG[r.priority] || PRIORITY_CONFIG.medium;
        return (
          <Chip
            label={r.priority}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: 11,
              textTransform: "capitalize",
              backgroundColor: cfg.bg,
              color: cfg.color,
            }}
          />
        );
      },
    },
    {
      field: "project",
      headerName: "Lift",
      render: (r) =>
        r.project
          ? r.project.customer_name || `#${r.project.id}`
          : "-",
    },
    {
      field: "status",
      headerName: "Status",
      render: (r) => {
        const cfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.open;
        return (
          <Chip
            label={cfg.label}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: 11,
              backgroundColor: cfg.bg,
              color: cfg.color,
            }}
          />
        );
      },
    },
    {
      field: "created_at",
      headerName: "Created",
      render: (r) => {
        if (!r.created_at) return "-";
        return new Date(r.created_at).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      },
    },
  ];

  const actions = [
    {
      icon: <Visibility />,
      label: "View",
      onClick: (row) => navigate(`/support-tickets/${row.id}`),
      backgroundColor: theme.palette.primary.main,
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box component="h1" sx={{ m: 0 }}>
          Support Tickets
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          sx={{
            borderRadius: theme.button.borderRadius,
            backgroundColor: theme.palette.primary.main,
          }}
          onClick={() => setOpenCreate(true)}
        >
          Create Ticket
        </Button>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab label={`All (${counts.all || 0})`} />
          <Tab label={`Open (${counts.open || 0})`} />
          <Tab label={`In Progress (${counts.in_progress || 0})`} />
          <Tab label={`Resolved (${counts.resolved || 0})`} />
          <Tab label={`Closed (${counts.closed || 0})`} />
        </Tabs>
      </Box>

      {/* Filters */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          mb: 3,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <form
          onSubmit={handleSearchSubmit}
          style={{ display: "flex", gap: "8px" }}
        >
          <TextField
            size="small"
            placeholder="Ticket #, subject or customer"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ backgroundColor: "#fff", borderRadius: 1, width: 260 }}
          />
          <Button
            type="submit"
            variant="contained"
            sx={{ minWidth: "40px", p: 1 }}
          >
            <SearchIcon />
          </Button>
          {(searchInput || filters.search) && (
            <Button
              type="button"
              variant="outlined"
              onClick={handleClearSearch}
              sx={{ minWidth: "40px", p: 1, backgroundColor: "#fff" }}
            >
              <ClearIcon />
            </Button>
          )}
        </form>

        <TextField
          select
          size="small"
          label="Category"
          value={filters.category}
          onChange={(e) => setFilters({ ...filters, category: e.target.value })}
          sx={{ minWidth: 160, backgroundColor: "#fff" }}
        >
          <MenuItem value="">All Categories</MenuItem>
          <MenuItem value="maintenance">Maintenance</MenuItem>
          <MenuItem value="breakdown">Breakdown</MenuItem>
          <MenuItem value="billing">Billing</MenuItem>
          <MenuItem value="general">General</MenuItem>
        </TextField>

        <TextField
          select
          size="small"
          label="Priority"
          value={filters.priority}
          onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
          sx={{ minWidth: 140, backgroundColor: "#fff" }}
        >
          <MenuItem value="">All Priorities</MenuItem>
          <MenuItem value="low">Low</MenuItem>
          <MenuItem value="medium">Medium</MenuItem>
          <MenuItem value="high">High</MenuItem>
          <MenuItem value="urgent">Urgent</MenuItem>
        </TextField>

        <TextField
          type="date"
          size="small"
          label="From Date"
          InputLabelProps={{ shrink: true }}
          value={filters.from_date}
          onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
          sx={{ backgroundColor: "#fff" }}
        />

        <TextField
          type="date"
          size="small"
          label="To Date"
          InputLabelProps={{ shrink: true }}
          value={filters.to_date}
          onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
          sx={{ backgroundColor: "#fff" }}
        />

        {hasActiveFilters && (
          <Button variant="text" onClick={handleClearFilters}>
            Clear Filters
          </Button>
        )}

        <Box sx={{ flex: 1 }} />

        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          disabled={exporting}
          sx={{ backgroundColor: "#fff" }}
        >
          {exporting ? "Exporting..." : "Export Excel"}
        </Button>
      </Box>

      {/* Table */}
      <Box>
        <TableComponent
          data={tickets || []}
          columns={columns}
          actions={actions}
          loading={loading}
          links={links}
          setCurrentPage={setCurrentPage}
          meta={meta}
          perPage={perPage}
          setPerPage={setPerPage}
        />
      </Box>

      <CreateTicketDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        saving={creating}
        onSave={handleCreate}
        errors={createErrors}
      />
    </Box>
  );
};

export default TicketsManagementPage;
